import { useState, useEffect, useRef, useCallback } from "react";
import config from "../config";

const api      = config.API_BASE_URL;
const chatMgmt = config.API_BASE_URL;

const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// ── Profile picture cache ─────────────────────────────────────
const picCache = {};

// ── Get logged-in admin info from localStorage ────────────────
const getAdminInfo = () => {
  try {
    const data = JSON.parse(localStorage.getItem("admin") || "{}");
    return {
      username: data?.username || data?.claimUsername || data?.identificationCode || "admin",
      role:     localStorage.getItem("userRole") || data?.role || "admin",
    };
  } catch { return { username: "admin", role: "admin" }; }
};

const normalizeU = (u) => (u || "").toLowerCase().trim().replace(/^admin_/, "");
const matchU     = (a, b) => normalizeU(a) === normalizeU(b);

// ── Avatar with profile picture fetching ─────────────────────
function Avatar({ name, size = 36, online = false }) {
  const colors = ["#0084ff","#00b09b","#f7971e","#e040fb","#f44336","#43a047","#ff5722","#3f51b5"];
  const color  = colors[name?.charCodeAt(0) % colors.length] ?? "#0084ff";
  const [imgSrc,   setImgSrc]   = useState(picCache[name] ?? null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!name) return;
    if (picCache[name] === "none") { setImgSrc(null); return; }
    if (picCache[name])            { setImgSrc(picCache[name]); return; }
    fetch(`${api}/Users/get-profile-picture/${encodeURIComponent(name)}`)
      .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
      .then(blob => { const url = URL.createObjectURL(blob); picCache[name] = url; setImgSrc(url); setImgError(false); })
      .catch(() => { picCache[name] = "none"; setImgSrc(null); setImgError(true); });
  }, [name]);

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      {imgSrc && !imgError ? (
        <img src={imgSrc} alt={name}
          onError={() => { setImgError(true); picCache[name] = "none"; }}
          style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", display: "block", border: "2px solid rgba(255,255,255,0.15)" }} />
      ) : (
        <div style={{ width: size, height: size, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: size * 0.38, userSelect: "none" }}>
          {name?.[0]?.toUpperCase() ?? "?"}
        </div>
      )}
      {online && (
        <span style={{ position: "absolute", bottom: 1, right: 1, width: size * 0.28, height: size * 0.28, borderRadius: "50%", background: "#31a24c", border: "2px solid #1a1a2e" }} />
      )}
    </div>
  );
}

function ImagePreview({ file, onRemove }) {
  const [url] = useState(() => URL.createObjectURL(file));
  return (
    <div style={{ position: "relative", display: "inline-block", margin: "4px 0" }}>
      <img src={url} alt="preview" style={{ height: 60, borderRadius: 8, border: "2px solid #0084ff", objectFit: "cover" }} />
      <button onClick={onRemove} style={{ position: "absolute", top: -5, right: -5, background: "#e41e3f", color: "#fff", border: "2px solid #1a1a2e", borderRadius: "50%", width: 18, height: 18, cursor: "pointer", fontSize: 9, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ── Mini Chat Window ──────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
function MiniChatWindow({ username, adminUsername, onClose, isDark, zIndex }) {
  const [messages,       setMessages]       = useState([]);
  const [input,          setInput]          = useState("");
  const [sending,        setSending]        = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [imageFile,      setImageFile]      = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [replyTo,        setReplyTo]        = useState(null);
  const [minimized,      setMinimized]      = useState(false);
  const [unread,         setUnread]         = useState(0);

  // ── Claim state ───────────────────────────────────────────
  const [claimLoading, setClaimLoading] = useState(true);
  const [claimStatus,  setClaimStatus]  = useState({ assigned: false, assignedTo: null });
  const [claiming,     setClaiming]     = useState(false);

  const { username: myUsername, role: myRole } = getAdminInfo();
  const isSuperAdmin = myRole?.toLowerCase() === "superadmin";
  const isMyClaim    = claimStatus.assigned && matchU(claimStatus.assignedTo, myUsername);
  const canSendMsg   = isSuperAdmin || isMyClaim;

  const bottomRef  = useRef(null);
  const fileRef    = useRef(null);
  const pollRef    = useRef(null);
  const claimTimer = useRef(null);

  const th = {
    bg:     isDark ? "#1a1a2e" : "#fff",
    border: isDark ? "#2d2d4e" : "#e4e6eb",
    input:  isDark ? "#0f3460" : "#f0f2f5",
    text:   isDark ? "#e0e0e0" : "#050505",
    sub:    isDark ? "#7a7a9a" : "#8a8d91",
    bubble: isDark ? "#2d2d4e" : "#f0f2f5",
  };

  // ── Fetch claim ────────────────────────────────────────────
  const fetchClaim = useCallback(async () => {
    try {
      const res = await fetch(`${chatMgmt}/ChatManagement/claim/${encodeURIComponent(username)}`);
      if (!res.ok) return;
      const data = await res.json();
      setClaimStatus({ assigned: !!data.assigned, assignedTo: data.assignedTo ?? null });
    } catch {}
    finally { setClaimLoading(false); }
  }, [username]);

  // ── Claim conversation ────────────────────────────────────
  const claimConversation = async (e) => {
    e?.stopPropagation();
    if (claiming) return;
    setClaiming(true);
    try {
      const res = await fetch(`${chatMgmt}/ChatManagement/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, adminUsername: myUsername, role: myRole }),
      });
      const data = await res.json();
      if (res.ok) setClaimStatus({ assigned: true, assignedTo: data.assignedTo ?? myUsername });
      else alert(`⚠️ ${data.message ?? "Could not claim this chat."}`);
    } catch (err) { console.error(err); }
    finally { setClaiming(false); }
  };

  // ── Fetch messages ─────────────────────────────────────────
  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${api}/Messages/admin/${adminUsername}/user/${username}`);
      if (!res.ok) return;
      const data = await res.json();
      const sorted = data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setMessages(sorted);
      if (minimized) setUnread(sorted.filter(m => m.senderType === "user" && !m.read).length);
    } catch {}
    finally { setLoading(false); }
  }, [username, adminUsername, minimized]);

  const markRead = useCallback(async () => {
    try { await fetch(`${api}/Messages/markAllAsRead/${username}`, { method: "POST" }); setUnread(0); } catch {}
  }, [username]);

  useEffect(() => {
    fetchMessages();
    fetchClaim();
    pollRef.current   = setInterval(fetchMessages, 4000);
    claimTimer.current = setInterval(fetchClaim, 8000);
    return () => { clearInterval(pollRef.current); clearInterval(claimTimer.current); };
  }, [fetchMessages, fetchClaim]);

  useEffect(() => {
    if (!minimized) { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); markRead(); }
  }, [messages, minimized]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending || !canSendMsg) return;
    setSending(true);
    try {
      await fetch(`${api}/Messages/sendAdmin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: username, admin: adminUsername,
          content: replyTo ? `↩ "${replyTo.content?.slice(0, 30)}" — ${text}` : text,
        }),
      });
      setInput(""); setReplyTo(null); await fetchMessages();
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const sendImage = async () => {
    if (!imageFile || !canSendMsg) return;
    setUploadingImage(true);
    try {
      const base64 = await new Promise(resolve => {
        const reader = new FileReader(); reader.onload = ev => resolve(ev.target.result); reader.readAsDataURL(imageFile);
      });
      await fetch(`${api}/Messages/sendAdmin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: username, admin: adminUsername, content: `[IMAGE]${base64}`, senderType: "admin", fileName: imageFile.name, fileType: imageFile.type, fileData: base64.split(",")[1], fileSize: imageFile.size }),
      });
      setImageFile(null); await fetchMessages();
    } catch (e) { console.error(e); }
    finally { setUploadingImage(false); }
  };

  const canSend = (input.trim() || imageFile) && !sending && !uploadingImage && canSendMsg;

  // ── Lock/Claim banner ──────────────────────────────────────
  const renderAccessBanner = () => {
    if (isSuperAdmin) return null; // superadmin: no banner needed, always can send

    if (claimLoading) return (
      <div style={{ padding: "7px 12px", background: isDark ? "#0f1a2e" : "#f0f4ff", borderBottom: `1px solid ${th.border}`, display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: th.sub }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", border: "1.5px solid transparent", borderTop: "1.5px solid #0084ff", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
        Checking access...
      </div>
    );

    if (!claimStatus.assigned) return (
      <div style={{ padding: "8px 12px", background: isDark ? "rgba(0,132,255,0.08)" : "rgba(0,132,255,0.06)", borderBottom: `2px solid rgba(0,132,255,0.4)`, display: "flex", alignItems: "center", gap: 8, animation: "popIn 0.2s ease" }}>
        <span style={{ fontSize: 16 }}>🔒</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#0084ff" }}>Unclaimed conversation</div>
          <div style={{ fontSize: 10, color: th.sub }}>Claim first to reply</div>
        </div>
        <button onClick={claimConversation} disabled={claiming}
          style={{ background: claiming ? "#555" : "linear-gradient(135deg,#0084ff,#006fde)", color: "#fff", border: "none", borderRadius: 12, padding: "5px 12px", fontSize: 11, fontWeight: 700, cursor: claiming ? "wait" : "pointer", flexShrink: 0, display: "flex", alignItems: "center", gap: 5, boxShadow: "0 2px 8px rgba(0,132,255,0.4)", transition: "all 0.2s", whiteSpace: "nowrap" }}>
          {claiming
            ? <><div style={{ width: 10, height: 10, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.3)", borderTop: "1.5px solid #fff", animation: "spin 0.8s linear infinite" }} /> Claiming...</>
            : "🙋 Claim"}
        </button>
      </div>
    );

    if (isMyClaim) return (
      <div style={{ padding: "6px 12px", background: isDark ? "rgba(34,197,94,0.07)" : "rgba(34,197,94,0.06)", borderBottom: `1px solid rgba(34,197,94,0.25)`, display: "flex", alignItems: "center", gap: 6, fontSize: 11, animation: "popIn 0.2s ease" }}>
        <span style={{ color: "#22c55e", fontWeight: 700 }}>✅ Assigned to me</span>
        <span style={{ color: th.sub }}>· You can reply</span>
      </div>
    );

    // Claimed by someone else — view only
    return (
      <div style={{ padding: "8px 12px", background: isDark ? "rgba(247,151,30,0.08)" : "rgba(247,151,30,0.06)", borderBottom: `2px solid rgba(247,151,30,0.45)`, display: "flex", alignItems: "center", gap: 8, animation: "popIn 0.2s ease" }}>
        <span style={{ fontSize: 16 }}>🔐</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#f7971e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Handled by {claimStatus.assignedTo}
          </div>
          <div style={{ fontSize: 10, color: th.sub }}>View-only mode</div>
        </div>
        <span style={{ background: "rgba(247,151,30,0.15)", color: "#f7971e", borderRadius: 10, padding: "3px 9px", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
          👁 View Only
        </span>
      </div>
    );
  };

  // ── Status label in header ─────────────────────────────────
  const headerStatus = () => {
    if (isSuperAdmin) return <span style={{ fontSize: 10, color: "rgba(255,255,255,0.85)", fontWeight: 700 }}>⭐ SuperAdmin</span>;
    if (claimLoading) return <span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>Checking...</span>;
    if (!claimStatus.assigned) return <span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>🔒 Unclaimed</span>;
    if (isMyClaim) return <span style={{ fontSize: 10, color: "#90ee90" }}>✅ Your chat</span>;
    return <span style={{ fontSize: 10, color: "#ffd580", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🔐 {claimStatus.assignedTo}</span>;
  };

  return (
    <div style={{ width: 300, background: th.bg, borderRadius: "12px 12px 0 0", border: `1px solid ${th.border}`, borderBottom: "none", boxShadow: "0 -4px 24px rgba(0,0,0,0.35)", display: "flex", flexDirection: "column", overflow: "hidden", zIndex, animation: "popUp 0.25s cubic-bezier(0.34,1.56,0.64,1)" }}>

      {/* ── Header ── */}
      <div onClick={() => { setMinimized(v => !v); if (minimized) markRead(); }}
        style={{ background: "linear-gradient(135deg, #0084ff, #006fde)", padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
        <Avatar name={username} size={30} online />
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{username}</span>
          {headerStatus()}
        </div>
        {minimized && unread > 0 && (
          <span style={{ background: "#e41e3f", color: "#fff", borderRadius: "50%", width: 18, height: 18, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", animation: "pulse 1.5s infinite" }}>{unread > 9 ? "9+" : unread}</span>
        )}
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={e => { e.stopPropagation(); setMinimized(v => !v); if (minimized) markRead(); }}
            style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", color: "#fff", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {minimized ? "▲" : "▼"}
          </button>
          <button onClick={e => { e.stopPropagation(); onClose(); }}
            style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", color: "#fff", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
      </div>

      {/* ── Chat Body ── */}
      {!minimized && (
        <>
          {/* 🔒 Access banner */}
          {renderAccessBanner()}

          {/* Messages */}
          <div style={{ height: 300, overflowY: "auto", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 2, background: th.bg }}>
            {loading ? (
              <div style={{ textAlign: "center", color: th.sub, paddingTop: 30, fontSize: 12 }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", border: "2px solid #333", borderTop: "2px solid #0084ff", animation: "spin 0.8s linear infinite", margin: "0 auto 8px" }} />
                Loading...
              </div>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: "center", paddingTop: 30 }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                  <Avatar name={username} size={56} />
                </div>
                <div style={{ color: th.text, fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{username}</div>
                <div style={{ color: th.sub, fontSize: 12 }}>
                  {canSendMsg ? "Say hi! 👋" : "👁 View-only mode"}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => {
                const isAdmin    = msg.senderType === "admin";
                const isImage    = msg.content?.startsWith("[IMAGE]");
                const imageUrl   = isImage ? msg.content.replace("[IMAGE]", "").trim() : null;
                const prev       = messages[i - 1];
                const sameAuthor = prev && prev.senderType === msg.senderType;
                return (
                  <div key={msg.messageId ?? i} style={{ display: "flex", flexDirection: isAdmin ? "row-reverse" : "row", alignItems: "flex-end", gap: 4, marginTop: sameAuthor ? 1 : 8, animation: "popIn 0.15s ease" }}>
                    {!isAdmin && !sameAuthor && <Avatar name={msg.user} size={22} />}
                    {!isAdmin && sameAuthor  && <div style={{ width: 22 }} />}
                    <div style={{ maxWidth: "75%", display: "flex", flexDirection: "column", alignItems: isAdmin ? "flex-end" : "flex-start" }}>
                      {msg.content?.startsWith("↩") && (
                        <div style={{ fontSize: 10, color: th.sub, borderLeft: "2px solid #0084ff", paddingLeft: 4, marginBottom: 2, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {msg.content.split("—")[0]}
                        </div>
                      )}
                      {isImage ? (
                        <div style={{ borderRadius: 10, overflow: "hidden", maxWidth: 180, cursor: "pointer" }} onClick={() => window.open(imageUrl, "_blank")}>
                          <img src={imageUrl} alt="shared" style={{ width: "100%", display: "block" }} />
                        </div>
                      ) : (
                        <div
                          style={{ background: isAdmin ? "#0084ff" : th.bubble, color: isAdmin ? "#fff" : th.text, borderRadius: isAdmin ? `14px 4px ${sameAuthor ? "4px" : "14px"} 14px` : `4px 14px 14px ${sameAuthor ? "4px" : "14px"}`, padding: "7px 11px", fontSize: 13, lineHeight: 1.4, wordBreak: "break-word", boxShadow: isAdmin ? "0 1px 3px rgba(0,132,255,0.2)" : "0 1px 2px rgba(0,0,0,0.1)", cursor: canSendMsg ? "pointer" : "default" }}
                          onDoubleClick={() => canSendMsg && setReplyTo(msg)}
                          title={canSendMsg ? "Double-click to reply" : ""}
                        >{msg.content}</div>
                      )}
                      <span style={{ fontSize: 9, color: th.sub, marginTop: 1, paddingLeft: 2, paddingRight: 2 }}>
                        {formatTime(msg.timestamp)}{isAdmin && msg.read && " ✓"}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* Reply bar — only if can send */}
          {replyTo && canSendMsg && (
            <div style={{ padding: "4px 10px", background: isDark ? "#1e2a3e" : "#eef4ff", borderTop: `1px solid ${th.border}`, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "#0084ff", fontSize: 13 }}>↩</span>
              <span style={{ flex: 1, fontSize: 11, color: th.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {replyTo.content?.startsWith("[IMAGE]") ? "📷 Photo" : replyTo.content?.slice(0, 40)}
              </span>
              <button onClick={() => setReplyTo(null)} style={{ background: "none", border: "none", cursor: "pointer", color: th.sub, fontSize: 14 }}>✕</button>
            </div>
          )}

          {/* Image preview — only if can send */}
          {imageFile && canSendMsg && (
            <div style={{ padding: "4px 10px", borderTop: `1px solid ${th.border}`, background: th.bg }}>
              <ImagePreview file={imageFile} onRemove={() => setImageFile(null)} />
            </div>
          )}

          {/* ── Input bar ── */}
          <div style={{
            padding: "8px 10px", borderTop: `1px solid ${th.border}`,
            display: "flex", alignItems: "center", gap: 6, background: th.bg,
            // Dim + disable if not allowed
            opacity: canSendMsg ? 1 : 0.42,
            pointerEvents: canSendMsg ? "auto" : "none",
            filter: canSendMsg ? "none" : "grayscale(70%)",
            transition: "opacity 0.25s, filter 0.25s",
            position: "relative",
          }}>
            <button onClick={() => fileRef.current?.click()}
              style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", padding: 2, transition: "transform 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.2)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
              <svg width="18" height="18" fill="none" stroke="#0084ff" viewBox="0 0 24 24" strokeWidth={2}>
                <rect x="3" y="3" width="18" height="18" rx="3"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="#0084ff" stroke="none"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 15l-5-5L5 21"/>
              </svg>
            </button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={e => { const f = e.target.files[0]; if (f) setImageFile(f); e.target.value = ""; }} />

            <div style={{ flex: 1, background: th.input, borderRadius: 18, padding: "7px 12px" }}>
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") { e.preventDefault(); if (imageFile) sendImage(); else sendMessage(); }
                  if (e.key === "Escape") setReplyTo(null);
                }}
                placeholder={
                  !canSendMsg
                    ? (!claimStatus.assigned ? "🔒 Claim chat to reply..." : `🔐 Only ${claimStatus.assignedTo} can reply...`)
                    : imageFile ? "Caption..." : replyTo ? "Reply..." : "Aa"
                }
                style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontSize: 13, color: th.text }}
              />
            </div>

            {!canSend ? (
              <button style={{ background: "none", border: "none", cursor: "default", fontSize: 20, color: isDark ? "#555" : "#ccc" }}>👍</button>
            ) : (
              <button onClick={imageFile ? sendImage : sendMessage} disabled={!canSend}
                style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,132,255,0.4)", transition: "transform 0.2s", flexShrink: 0 }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                {sending || uploadingImage
                  ? <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", animation: "spin 0.8s linear infinite" }} />
                  : <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>}
              </button>
            )}
          </div>

          {/* Overlay lock icon when not allowed (extra visual hint) */}
          {!canSendMsg && !claimLoading && (
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0, height: 52,
              display: "flex", alignItems: "center", justifyContent: "center",
              pointerEvents: "none",
            }}>
              <span style={{ fontSize: 18, opacity: 0.18 }}>
                {!claimStatus.assigned ? "🔒" : "🔐"}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ── Conversation List Popup ───────────────────────────────────
// ─────────────────────────────────────────────────────────────
function ConversationListPopup({ adminUsername, onOpenChat, isDark, onClose }) {
  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState("");

  const th = {
    bg:    isDark ? "#16213e" : "#fff",
    border:isDark ? "#2d2d4e" : "#e4e6eb",
    text:  isDark ? "#e0e0e0" : "#050505",
    sub:   isDark ? "#7a7a9a" : "#8a8d91",
    input: isDark ? "#0f3460" : "#f0f2f5",
    hover: isDark ? "#22223a" : "#f0f2f5",
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const res = await fetch(`${api}/Messages/admin/${adminUsername}`);
        if (!res.ok) return;
        const data = await res.json();
        const grouped = {};
        data.forEach(msg => { if (!grouped[msg.user]) grouped[msg.user] = []; grouped[msg.user].push(msg); });
        const convs = await Promise.all(Object.entries(grouped).map(async ([username, msgs]) => {
          const sorted = [...msgs].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          let unreadCount = 0;
          try {
            const r = await fetch(`${api}/Messages/admin/${adminUsername}/user/${username}/unread`);
            if (r.ok) { const d = await r.json(); unreadCount = d.count ?? 0; }
          } catch {}
          return { username, lastMessage: sorted[0]?.content, lastTimestamp: sorted[0]?.timestamp, unreadCount };
        }));
        convs.sort((a, b) => new Date(b.lastTimestamp ?? 0) - new Date(a.lastTimestamp ?? 0));
        setConversations(convs);
      } catch (e) { console.error(e); }
    };
    fetchAll();
    const interval = setInterval(fetchAll, 5000);
    return () => clearInterval(interval);
  }, [adminUsername]);

  const filtered    = conversations.filter(c => c.username.toLowerCase().includes(search.toLowerCase()));
  const totalUnread = conversations.reduce((s, c) => s + c.unreadCount, 0);

  return (
    <div style={{ width: 300, maxHeight: 480, background: th.bg, borderRadius: "12px 12px 0 0", border: `1px solid ${th.border}`, borderBottom: "none", boxShadow: "0 -4px 24px rgba(0,0,0,0.35)", display: "flex", flexDirection: "column", overflow: "hidden", animation: "popUp 0.25s cubic-bezier(0.34,1.56,0.64,1)" }}>
      <div style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>
          💬 Chats {totalUnread > 0 && <span style={{ background: "#e41e3f", borderRadius: "50%", padding: "1px 6px", fontSize: 11, marginLeft: 4 }}>{totalUnread}</span>}
        </span>
        <button onClick={onClose} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%", width: 24, height: 24, cursor: "pointer", color: "#fff", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
      </div>

      <div style={{ padding: "8px 10px", borderBottom: `1px solid ${th.border}` }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍  Search..."
          style={{ width: "100%", boxSizing: "border-box", background: th.input, border: "none", borderRadius: 18, padding: "7px 14px", fontSize: 13, outline: "none", color: th.text }} />
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {filtered.length === 0
          ? <div style={{ textAlign: "center", color: th.sub, padding: 24, fontSize: 13 }}>No conversations</div>
          : filtered.map(conv => (
            <div key={conv.username} onClick={() => onOpenChat(conv.username)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", cursor: "pointer", transition: "background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background = th.hover}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <Avatar name={conv.username} size={42} online />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontWeight: conv.unreadCount > 0 ? 700 : 500, fontSize: 13, color: th.text }}>{conv.username}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    {conv.lastTimestamp && <span style={{ fontSize: 10, color: th.sub }}>{formatTime(conv.lastTimestamp)}</span>}
                    {conv.unreadCount > 0 && (
                      <span style={{ background: "#0084ff", color: "#fff", borderRadius: "50%", width: 18, height: 18, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, animation: "pulse 1.5s infinite" }}>
                        {conv.unreadCount > 9 ? "9+" : conv.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: conv.unreadCount > 0 ? th.text : th.sub, fontWeight: conv.unreadCount > 0 ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {conv.lastMessage?.startsWith("[IMAGE]") ? "📷 Photo" : (conv.lastMessage ?? "No messages yet")}
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ── MAIN: AdminMessengerBubble ────────────────────────────────
// ─────────────────────────────────────────────────────────────
export default function AdminMessengerBubble({ adminUsername: _adminUsername, isDarkDefault = true }) {
  const { username: resolvedAdmin } = getAdminInfo();
  const adminUsername = _adminUsername || resolvedAdmin || "admin";

  const [isDark,      setIsDark]      = useState(isDarkDefault);
  const [showList,    setShowList]    = useState(false);
  const [openChats,   setOpenChats]   = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${api}/Messages/admin/${adminUsername}`);
        if (!res.ok) return;
        const data = await res.json();
        const grouped = {};
        data.forEach(msg => { if (!grouped[msg.user]) grouped[msg.user] = []; grouped[msg.user].push(msg); });
        let total = 0;
        await Promise.all(Object.keys(grouped).map(async username => {
          try { const r = await fetch(`${api}/Messages/admin/${adminUsername}/user/${username}/unread`); if (r.ok) { const d = await r.json(); total += d.count ?? 0; } } catch {}
        }));
        setTotalUnread(total);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 5000);
    return () => clearInterval(interval);
  }, [adminUsername]);

  const openChat  = (username) => { setShowList(false); if (!openChats.includes(username)) setOpenChats(prev => [...prev.slice(-2), username]); };
  const closeChat = (username) => setOpenChats(prev => prev.filter(u => u !== username));

  return (
    <>
      <style>{`
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes popIn  { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes popUp  { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes pulse  { 0%,100% { box-shadow: 0 0 0 0 rgba(0,132,255,0.5); } 70% { box-shadow: 0 0 0 8px rgba(0,132,255,0); } }
        @keyframes bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: #444; border-radius: 3px; }
      `}</style>

      <div style={{ position: "fixed", bottom: 0, right: 24, display: "flex", alignItems: "flex-end", gap: 12, zIndex: 9999 }}>
        {openChats.map((username, idx) => (
          <MiniChatWindow key={username} username={username} adminUsername={adminUsername}
            onClose={() => closeChat(username)} isDark={isDark} zIndex={9990 + idx} />
        ))}

        {showList && (
          <ConversationListPopup adminUsername={adminUsername} onOpenChat={openChat} isDark={isDark} onClose={() => setShowList(false)} />
        )}

        <div style={{ paddingBottom: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <button onClick={() => setIsDark(v => !v)} title={isDark ? "Light mode" : "Dark mode"}
            style={{ width: 36, height: 36, borderRadius: "50%", background: isDark ? "#0f3460" : "#e4e6eb", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.3)", transition: "transform 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
            {isDark ? "☀️" : "🌙"}
          </button>

          <button onClick={() => setShowList(v => !v)}
            style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg,#0084ff,#006fde)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 20px rgba(0,132,255,0.55)", position: "relative", transition: "transform 0.2s", animation: totalUnread > 0 && !showList ? "pulse 2s infinite" : "none" }}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
            title="Messenger">
            {showList
              ? <svg width="24" height="24" fill="#fff" viewBox="0 0 24 24"><path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/></svg>
              : <svg width="26" height="26" viewBox="0 0 48 48" fill="#fff"><path d="M24 4C12.95 4 4 12.28 4 22.5c0 5.63 2.68 10.66 6.9 14.07V44l6.77-3.72A21.6 21.6 0 0024 41c11.05 0 20-8.28 20-18.5S35.05 4 24 4zm2.1 24.92l-5.1-5.44-9.95 5.44 10.95-11.63 5.23 5.44 9.82-5.44-10.95 11.63z"/></svg>}
            {totalUnread > 0 && !showList && (
              <span style={{ position: "absolute", top: -2, right: -2, background: "#e41e3f", color: "#fff", borderRadius: "50%", width: 22, height: 22, fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff", boxShadow: "0 2px 6px rgba(228,30,63,0.6)", animation: "bounce 1s infinite" }}>
                {totalUnread > 9 ? "9+" : totalUnread}
              </span>
            )}
          </button>
        </div>
      </div>
    </>
  );
}